import React, { useEffect, useRef } from 'react'
import { formatPhone } from '@/lib/format'
import { Phone, PhoneOff, MessageSquare } from 'lucide-react'
import FocusTrap from 'focus-trap-react'
import type { CallerInfo } from '@/stores/callStore'
import { SafeAreaInsets } from '@/components/ui/SafeAreaInsets'
import { SafeAreaBottom } from '@/components/ui/SafeAreaBottom'
import { AvatarStateRing } from './AvatarStateRing'

interface MobileIncomingCallOverlayProps {
  callerInfo: CallerInfo | null
  onAccept: () => void
  onDecline: () => void
  onMessage?: () => void
}

/**
 * Full-screen incoming-call sheet. Direction B layout:
 *  - centered AvatarStateRing (white ringing pulse — an incoming call needs you now)
 *  - `.date-hero` caller name
 *  - round iOS answer keys (red Decline, green Accept) at the safe-area bottom
 *
 * iOS safe-area: the bottom button group is wrapped in SafeAreaBottom so
 * the home indicator never overlaps the call-control hit area
 * (CLAUDE.md §8; FOR-1544/1584/1597).
 */
export function MobileIncomingCallOverlay({
  callerInfo,
  onAccept,
  onDecline,
  onMessage,
}: MobileIncomingCallOverlayProps) {
  const acceptBtnRef = useRef<HTMLButtonElement>(null)
  const vibrateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    acceptBtnRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDecline()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onDecline])

  // navigator.vibrate — feature-detect, repeat until unmount
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.vibrate) return
    const pattern = [200, 100, 200, 100, 200]
    navigator.vibrate(pattern)
    vibrateIntervalRef.current = setInterval(() => {
      navigator.vibrate(pattern)
    }, 2000)
    return () => {
      if (vibrateIntervalRef.current) clearInterval(vibrateIntervalRef.current)
      navigator.vibrate(0)
    }
  }, [])

  return (
    <FocusTrap focusTrapOptions={{ returnFocusOnDeactivate: true }}>
      <SafeAreaInsets
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-caller-name"
        aria-describedby="mobile-call-status"
        // 100dvh keeps the bottom buttons inside the visible viewport when iOS
        // Safari's URL bar collapses (FND-M-016).
        className="md:hidden fixed inset-0 z-[9999] flex flex-col items-stretch justify-between animate-fade-up"
        style={{ background: 'var(--midnight-bg)', height: '100dvh' }}
        minTop={32}
        minBottom={0}
        minLeft={20}
        minRight={20}
      >
        {/* Backdrop blur sheen — soft white wash that sells "system event" */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at top, rgb(var(--fg-rgb) / 0.08) 0%, rgb(var(--bg-rgb) / 0) 60%)',
            pointerEvents: 'none',
          }}
        />

        {/* ── Hero column: avatar + caller name ────────────────────────── */}
        <div
          className="relative z-10 flex flex-col items-center"
          style={{ gap: 24, paddingTop: 8 }}
        >
          <p
            id="mobile-call-status"
            className="eyebrow"
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--midnight-text3)',
            }}
          >
            Incoming call
          </p>

          <AvatarStateRing
            name={callerInfo?.name ?? undefined}
            state="ringing"
            size="lg"
            pulse
          />

          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span
              id="mobile-caller-name"
              className="date-hero"
              style={{
                color: 'rgb(var(--fg-rgb))',
                fontSize: 'clamp(2rem, 7vw, 3.25rem)',
                lineHeight: 1.05,
                padding: '0 16px',
                overflowWrap: 'anywhere',
              }}
            >
              {callerInfo?.name || 'Unknown caller'}
            </span>
            {callerInfo?.number && (
              <span
                style={{
                  fontSize: 15,
                  color: 'rgb(var(--fg-rgb) / 0.60)',
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.02em',
                }}
              >
                {formatPhone(callerInfo.number)}
              </span>
            )}
          </div>
        </div>

        {/* ── Optional quick-message pill ──────────────────────────────── */}
        {onMessage && (
          <div className="relative z-10 flex justify-center" style={{ paddingBottom: 8 }}>
            <button
              type="button"
              onClick={onMessage}
              aria-label="Send quick message instead of answering"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 40,
                padding: '0 18px',
                borderRadius: 'var(--r-pill, 9999px)',
                border: '0.5px solid rgb(var(--fg-rgb) / 0.14)',
                background: 'rgb(var(--fg-rgb) / 0.06)',
                color: 'rgb(var(--fg-rgb) / 0.75)',
                // 16px floor — iOS Safari auto-zooms <16px on focused inputs.
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                touchAction: 'manipulation',
              }}
            >
              <MessageSquare style={{ width: 16, height: 16 }} />
              Message
            </button>
          </div>
        )}

        {/* ── Bottom: iOS answer keys, Decline red on the left and Accept
            green on the right, each labelled underneath. Accept breathes
            while it rings (.ring-key, still with reduced motion). ───────── */}
        <SafeAreaBottom extra={28} className="relative z-10 w-full">
          <div className="flex items-start justify-around px-6">
            <div className="flex flex-col items-center gap-2.5">
              <button
                type="button"
                onClick={onDecline}
                aria-label="Decline call"
                className="phone-end-key focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
                style={{ width: 76, height: 76, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              >
                <PhoneOff style={{ width: 30, height: 30 }} />
              </button>
              <span className="text-[14px] font-medium" style={{ color: 'rgb(var(--fg-rgb) / 0.8)' }}>Decline</span>
            </div>
            <div className="flex flex-col items-center gap-2.5">
              <button
                ref={acceptBtnRef}
                type="button"
                onClick={onAccept}
                aria-label="Accept call"
                className="phone-call-key ring-key focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
                style={{ width: 76, height: 76, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              >
                <Phone style={{ width: 30, height: 30 }} />
              </button>
              <span className="text-[14px] font-medium" style={{ color: 'rgb(var(--fg-rgb) / 0.8)' }}>Accept</span>
            </div>
          </div>
        </SafeAreaBottom>
      </SafeAreaInsets>
    </FocusTrap>
  )
}
